// src/app/services/logging.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from 'src/environments/environment';
@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private appInsights: ApplicationInsights | null = null;
  private logApiUrl: string | null = null;

  constructor(private http: HttpClient) {
    const loggingConfig = environment.logging;

    // Initialize Sentry if enabled
    if (loggingConfig.useSentry) {
      Sentry.init({
        dsn: loggingConfig.sentry.dsn,
      
        // This enables automatic instrumentation (highly recommended),
        // but is not necessary for purely manual usage
        // If you only want to use custom instrumentation:
        // * Remove the `BrowserTracing` integration
        // * add `Sentry.addTracingExtensions()` above your Sentry.init() call
        integrations: [Sentry.browserTracingIntegration()],
      
        // We recommend adjusting this value in production, or using tracesSampler
        // for finer control
        tracesSampleRate: 1.0,
      
        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        tracePropagationTargets: ["localhost:4200", /^https:\/\/yourserver\.io\/api/],
      });
      
    }

    // Initialize Azure Application Insights if enabled
    if (loggingConfig.useAppInsights) {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: loggingConfig.appInsights.instrumentationKey,
        },
      });
      this.appInsights.loadAppInsights();
    }

    // Set the API logging URL if enabled
    if (loggingConfig.useApi) {
      this.logApiUrl = loggingConfig.apiLogger.endpoint;
    }
  }

  // Capture exceptions for all enabled logging mechanisms
  captureException(error: any) {
    const loggingConfig = environment.logging;

    if (loggingConfig.useSentry) {
      Sentry.captureException(error);
    }
    if (loggingConfig.useAppInsights && this.appInsights) {
      this.appInsights.trackException({ exception: error });
    }

    if (loggingConfig.useApi && this.logApiUrl) {
      this.logErrorToApi('error', error.message || 'An error occurred', error.stack);
    }
  }

  // Capture custom messages for all enabled logging mechanisms
  captureMessage(message: string, level: Sentry.SeverityLevel='info') {
    const loggingConfig = environment.logging;

    if (loggingConfig.useSentry) {
      Sentry.captureMessage(message, level);
    }
    if (loggingConfig.useAppInsights && this.appInsights) {
      this.appInsights.trackTrace({
        message,
        severityLevel: this.mapSeverityToAppInsights(level),
      });
    }

    if (loggingConfig.useApi && this.logApiUrl) {
      this.logErrorToApi(level.toString(), message);
    }
  }

  // Track performance for all enabled logging mechanisms
  // startTransaction(name: string) {
  //   const loggingConfig = environment.logging;
  //   let sentryTransaction = null;

  //   if (loggingConfig.useSentry) {
  //     sentryTransaction = Sentry.startTransaction({ name });
  //   }

  //   if (loggingConfig.useAppInsights && this.appInsights) {
  //     this.appInsights.startTrackEvent(name);
  //   }

  //   return {
  //     finish: () => {
  //       if (sentryTransaction) {
  //         sentryTransaction.finish();
  //       }
  //       if (loggingConfig.useAppInsights && this.appInsights) {
  //         this.appInsights.stopTrackEvent(name);
  //       }
  //     },
  //   };
  // }

  // Add breadcrumbs/logs for all enabled logging mechanisms
  addBreadcrumb(message: string, category: string = 'default', level: Sentry.SeverityLevel='info') {
    const loggingConfig = environment.logging;

    if (loggingConfig.useSentry) {
      Sentry.addBreadcrumb({
        message,
        category,
        level,
      });
    }
    if (loggingConfig.useAppInsights && this.appInsights) {
      this.appInsights.trackTrace({
        message,
        severityLevel: this.mapSeverityToAppInsights(level),
      });
    }

    if (loggingConfig.useApi && this.logApiUrl) {
      this.logErrorToApi(level.toString(), message);
    }
  }

  // Helper method to log messages via API
  private logErrorToApi(level: string, message: string, stack?: string) {
    const logPayload = {
      level,
      message,
      stack: stack || null,
      timestamp: new Date().toISOString(),
    };

    this.http.post(this.logApiUrl as string, logPayload).subscribe({
      next: (response) => console.log('Log sent to API', response),
      error: (err) => console.error('Failed to send log to API', err),
    });
  }

  // Helper method to map Sentry severity levels to Azure Application Insights levels
  private mapSeverityToAppInsights(level: Sentry.SeverityLevel) {
    switch (level) {
      case 'fatal':
        return 4; // Error level in App Insights
      case 'error':
        return 3; // Error level
      case 'warning':
        return 2; // Warning level
      case 'info':
      case 'log':
        return 1; // Information level
      case 'debug':
      default:
        return 0; // Verbose level
    }
  }
}
